import { DataTypeProvider } from '@devexpress/dx-react-grid';
import { FEATURE_FLAGS } from '../../../constants/constants';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import NRLicenseDrawerContainer from '../../NRLicenseIssues/LicenseIssueDrawer/NRLicenseDrawerContainer';
import { useContext, useMemo } from 'react';
import { FiltersContext } from '../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import { useFlag } from '@openfeature/react-sdk';
import Tooltip from '@mui/material/Tooltip';
import { GenericContext } from '../../GenericContext/GenericContextWrapper';
import useFetchLicensesSpdxIds from '../../NRLicense/useFetchLicensesSpdxIds';
import NRMultipleLicensesIcon from '../../../NRIcons/NRMultipleLicensesIcon';

// Helper to tokenize the license expression while keeping separators/operators/parenthesis
const tokenizeLicenseExpression = expression => {
  if (!expression) return [];

  const tokens = [];
  const regex = /\b(AND|OR|WITH)\b|,|\(|\)|\s+/gi;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(expression)) !== null) {
    const matchedText = match[0];

    // If this is a potential logical operator, ensure it is surrounded by whitespace.
    // Otherwise, treat it as part of a larger token (e.g., "GPL-2.0-or-later").
    const isPotentialOperator = /^(AND|OR|WITH)$/i.test(matchedText);
    if (isPotentialOperator) {
      const start = match.index;
      const end = start + matchedText.length;

      const beforeChar = start === 0 ? ' ' : expression[start - 1];
      const afterChar = end >= expression.length ? ' ' : expression[end];

      const hasSpaceBefore = /\s/.test(beforeChar);
      const hasSpaceAfter = /\s/.test(afterChar);

      // If not surrounded by whitespace, skip this match so it remains in the candidate token.
      if (!hasSpaceBefore || !hasSpaceAfter) {
        regex.lastIndex = end;
        continue;
      }
    }

    if (match.index > lastIndex) {
      tokens.push({ type: 'candidate', raw: expression.slice(lastIndex, match.index) });
    }

    if (/^\s+$/.test(matchedText)) {
      tokens.push({ type: 'whitespace', raw: matchedText });
    } else if (matchedText === '(' || matchedText === ')') {
      tokens.push({ type: 'paren', raw: matchedText });
    } else if (matchedText === ',') {
      tokens.push({ type: 'comma', raw: matchedText });
    } else {
      tokens.push({ type: 'operator', raw: matchedText });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < expression.length) {
    tokens.push({ type: 'candidate', raw: expression.slice(lastIndex) });
  }

  return tokens;
};

const SBOMLicenseFormatter = ({ value, column, row }) => {
  const { value: isLicenseIssueEnable } = useFlag(FEATURE_FLAGS.LICENSE_ISSUES_FLAG, false);
  const filtersContext = useContext(FiltersContext);
  const genericContext = useContext(GenericContext);
  const theme = useTheme();
  
  // Check if FiltersContext is properly provided (not just default values)
  // Default context has openDrawer: null, but real context has boolean
  // Also check that drawerContent is an array (not null) to confirm it's the real context
  const isDrawerAvailable = filtersContext && 
                            filtersContext.setOpenDrawer && 
                            typeof filtersContext.setOpenDrawer === 'function' &&
                            filtersContext.openDrawer !== null && // Default context has null
                            Array.isArray(filtersContext.drawerContent); // Real context has array
  
  // Get licensesSpdxIds from GenericContext, handle gracefully if not available
  const licensesSpdxIds = genericContext?.licensesSpdxIds || null;

  const spdxIdSet = useMemo(() => {
    if (!licensesSpdxIds) return new Set();

    return new Set(
      licensesSpdxIds
        .map(item => {
          if (typeof item === 'string') return item.trim();
          // Support a few likely shapes just in case
          return (item?.licenseId || item?.id || item?.value || '').trim();
        })
        .filter(Boolean)
    );
  }, [licensesSpdxIds]);

  const tokens = useMemo(() => tokenizeLicenseExpression(value), [value]);

  const tokenMeta = useMemo(
    () =>
      tokens.map(token => {
        if (token.type !== 'candidate') return { ...token, clickable: false, isValidLicense: false };

        const trimmed = token.raw.trim();
        if (!trimmed) return { ...token, type: 'whitespace', clickable: false, isValidLicense: false };

        const isProprietary = trimmed.toLowerCase() === 'proprietary';
        const isValidSpdx = spdxIdSet.has(trimmed) && !isProprietary;

        return {
          ...token,
          type: 'license',
          value: trimmed,
          isValidLicense: isValidSpdx || isProprietary,
          isProprietary,
          clickable: isDrawerAvailable && isLicenseIssueEnable && isValidSpdx && !isProprietary
        };
      }),
    [tokens, spdxIdSet, isLicenseIssueEnable, isDrawerAvailable]
  );

  const validLicenseCount = useMemo(() => tokenMeta.filter(t => t.type === 'license' && t.isValidLicense && !t.isProprietary).length, [tokenMeta]);

  const handleOpenDrawer = license => {
    if (!isDrawerAvailable || !isLicenseIssueEnable || !row) {
      return;
    }

    // Behaviour preserved from previous implementation
    const { setOpenDrawer, setDrawerContent, drawerContent, setDrawerTitle, drawerTitle, setHasBack } = filtersContext;
    !!drawerContent.length && setHasBack(true);
    setDrawerTitle([...drawerTitle, `${license}`]);
    setDrawerContent([...drawerContent, <NRLicenseDrawerContainer spdxId={license} />]);
    setOpenDrawer(true);
  };

  const tooltipText = 'NetRise has found multiple licenses, but cannot determine the correct association between them.';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <Typography
        variant="body2"
        color={isLicenseIssueEnable ? theme.palette.text.secondary : theme.palette.text.primary}
        component="span"
        sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center' }}
      >
        {tokenMeta.map((token, index) => {
          if (token.type === 'whitespace') {
            return (
              <span key={index} style={{ marginRight: 4 }}>
                {token.raw}
              </span>
            );
          }

          if (token.type === 'operator' || token.type === 'paren' || token.type === 'comma') {
            // Just render as normal text, never clickable
            return <span key={index}>{token.raw}</span>;
          }

          if (token.type === 'license') {
            const { clickable, isProprietary, value: tokenValue } = token;
            const displayText = token.raw;

            const commonStyles = {
              textDecoration: clickable ? 'underline' : 'unset',
              cursor: clickable ? 'pointer' : 'default',
              color: isLicenseIssueEnable && clickable ? theme.palette.text.secondary : theme.palette.text.primary
            };

            return (
              <Box
                key={index}
                component="span"
                sx={commonStyles}
                onClick={event => {
                  if (!clickable) return;
                  event.stopPropagation();
                  handleOpenDrawer(tokenValue);
                }}
              >
                {displayText}
                {isProprietary && ''}
              </Box>
            );
          }

          return <span key={index}>{token.raw}</span>;
        })}
      </Typography>

      {validLicenseCount > 1 && row?.license?.length > 1 && (
        <Tooltip title={tooltipText} placement="top">
          <Box
            component="span"
            sx={{
              display: 'flex',
              ml: 'auto',
              pl: 1
            }}
          >
            <NRMultipleLicensesIcon height="20" width="20" />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export const NRSBOMLicenseProvider = props => (
  <DataTypeProvider formatterComponent={SBOMLicenseFormatter} {...props} availableFilterOperations={['equal']} />
);
